import { computed } from 'vue';
import { isDefined } from '@zoomcatalog/shared';
import { UserCompany } from '@/service/request-handlers/useUserCompanies';
import { UserDistributorCompany } from '@/service/request-handlers/useUserDistributorCompanies';
import { Company } from '@/service/ts-definitions';
import { useCommit, useSelector } from '@/store/rootStore';
import { storageInstances } from '@/utils/storageInstances';

export const isUserCompany = (
  userCompany: unknown
): userCompany is UserCompany => {
  return (
    typeof userCompany === 'object' &&
    userCompany !== null &&
    'is_supplier' in userCompany &&
    'company_id' in userCompany
  );
};

export function useCompanyStore() {
  const commit = useCommit();
  const selectedCompanyId = useSelector(
    (state) =>
      state.company.selectedCompanyId ?? storageInstances.lastCompanyId.get()
  );
  const company = useSelector((state) => state.company.company);
  const userCompanies = useSelector((state) => state.company.userCompanies);
  const distributorCompanies = useSelector(
    (state) => state.company.distributorCompanies
  );
  const selectedCompany = computed<
    UserCompany | UserDistributorCompany | undefined
  >(() => {
    const matchingUserCompany = userCompanies.value?.find(
      (company) => company.company_id === selectedCompanyId.value
    );
    if (matchingUserCompany) return matchingUserCompany;

    const matchingDistributorCompany = distributorCompanies.value?.find(
      (company) => company.company_id === selectedCompanyId.value
    );
    if (matchingDistributorCompany) return matchingDistributorCompany;

    return (
      userCompanies.value?.find(isDefined) ??
      distributorCompanies.value?.find(isDefined)
    );
  });

  function setCompany(company: Company) {
    commit({ type: 'company/setCompany', payload: company });
  }
  function setSelectedCompanyId(companyId: string) {
    storageInstances.lastCompanyId.set(companyId);
    commit({ type: 'company/setSelectedCompanyId', payload: companyId });
  }
  function setUserCompanies(userCompanies: UserCompany[]) {
    commit({ type: 'company/setUserCompanies', payload: userCompanies });
  }
  function setDistributorCompanies(
    distributorCompanies: UserDistributorCompany[]
  ) {
    commit({
      type: 'company/setDistributorCompanies',
      payload: distributorCompanies,
    });
  }

  return {
    setCompany,
    setSelectedCompanyId,
    setUserCompanies,
    setDistributorCompanies,
    selectedCompany,
    userCompanies,
    distributorCompanies,
    company,
  };
}
